import { useQuery } from '@tanstack/react-query'
import type { AdminDashboardSummary } from '@terafile/shared'
import { Link } from 'react-router-dom'
import { getAdminDashboardSummary } from '../lib/admin-api.js'
import { FETCH_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { LOADING } from '../lib/labels.js'

/**
 * 管理者ダッシュボード（エディトリアル × 司令室スタイル）。
 *
 * BRD §6.E「容量概況: 全社合計の使用量・プラン分布の俯瞰」に対応。
 * Projects.tsx / ProjectDetail.tsx と意匠を統一。
 */
export function AdminDashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-dashboard-summary'],
    queryFn: getAdminDashboardSummary,
    // 即時性より新しい数字感が大事。30秒ごとに再取得。
    staleTime: 30 * 1000,
  })

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-16 space-y-12 grain">
      <Hero generatedAt={data?.generatedAt ?? null} />

      {isLoading && <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>}
      {error && (
        <div className="border border-destructive/40 bg-destructive/5 rounded-md p-4 text-sm text-destructive">
          {toUserMessage(error, FETCH_ERROR_FALLBACK)}
        </div>
      )}

      {data && <DashboardContent data={data} />}
    </div>
  )
}

// ===========================================================================
// 視覚部品
// ===========================================================================

function Hero({ generatedAt }: { generatedAt: string | null }) {
  return (
    <section className="flex items-end justify-between gap-6 flex-wrap">
      <div className="min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground/80 mb-3">
          Operations · Overview
        </p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
          管理者ダッシュボード
        </h1>
        <p className="mt-4 text-sm text-muted-foreground max-w-prose">
          全テナントの容量・プラン分布・新規登録動向を俯瞰します。 ここでの操作はすべて
          admin_audit_logs に記録され、テナント側ログには残りません。
        </p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <Link
          to="/admin/tenants"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm hover:opacity-90 transition-opacity"
        >
          テナント一覧
          <span aria-hidden>→</span>
        </Link>
        {generatedAt && (
          <p className="text-[11px] text-muted-foreground/70 font-mono editorial-num">
            集計 {new Date(generatedAt).toLocaleString('ja-JP')}
          </p>
        )}
      </div>
    </section>
  )
}

function DashboardContent({ data }: { data: AdminDashboardSummary }) {
  return (
    <div className="space-y-12 md:space-y-16">
      {data.pendingPlanUpgradeRequests > 0 && (
        <Link
          to="/admin/plan-upgrade-requests"
          className="block border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors rounded-lg p-4"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wider text-amber-900 font-mono">
                Pending · 要対応
              </p>
              <p className="text-sm text-amber-900 mt-1">
                <span className="font-semibold editorial-num text-base">
                  {data.pendingPlanUpgradeRequests.toLocaleString('ja-JP')}
                </span>{' '}
                件の有料プラン申込みが対応待ちです
              </p>
            </div>
            <span className="text-amber-900 font-medium text-sm">確認する →</span>
          </div>
        </Link>
      )}

      <Section num="01" title="全社サマリ" hint="現時点の合計値">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6">
          <Stat label="全テナント" value={data.totalTenants} unit="件" />
          <Stat label="総ストレージ" valueRaw={formatBytes(BigInt(data.totalStorageBytes))} />
          <Stat label="プロジェクト" value={data.totalProjects} unit="件" />
          <Stat label="ファイル" value={data.totalFiles} unit="件" />
        </div>
      </Section>

      <Section
        num="02"
        title="分布"
        hint="プラン別 / ステータス別の内訳。バーは構成比、右端の数字が件数"
      >
        <div className="space-y-8">
          <DistributionRow
            label="プラン"
            items={[
              {
                label: '無料',
                value: data.tenantsByPlan.FREE,
                color: 'bg-foreground',
              },
              {
                label: '有料',
                value: data.tenantsByPlan.PRO,
                color: 'bg-emerald-600',
              },
              {
                label: '暫定',
                value: data.tenantsByPlan.EPHEMERAL,
                color: 'bg-amber-500',
              },
            ]}
          />
          <DistributionRow
            label="ステータス"
            items={[
              {
                label: '稼働中',
                value: data.tenantsByStatus.ACTIVE,
                color: 'bg-emerald-600',
              },
              {
                label: '60日凍結',
                value: data.tenantsByStatus.FROZEN,
                color: 'bg-amber-500',
              },
              {
                label: '規約凍結',
                value: data.tenantsByStatus.SUSPENDED,
                color: 'bg-destructive',
              },
              {
                label: '削除',
                value: data.tenantsByStatus.DELETED,
                color: 'bg-muted-foreground/40',
              },
            ]}
          />
        </div>
      </Section>

      <Section num="03" title="新規登録" hint="REGULAR テナントのみ集計">
        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
          <Stat label="過去 7 日" value={data.newTenantsLast7Days} unit="件" emphasize />
          <Stat label="過去 30 日" value={data.newTenantsLast30Days} unit="件" emphasize />
        </div>
      </Section>
    </div>
  )
}

function Section({
  num,
  title,
  hint,
  children,
}: {
  num: string
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-6">
      <div className="flex items-end gap-4 border-b border-border pb-4">
        <span className="font-mono text-xs text-muted-foreground/70 editorial-num pb-1">{num}</span>
        <div className="min-w-0">
          <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight">{title}</h2>
          {hint && <p className="text-xs text-muted-foreground mt-1 max-w-prose">{hint}</p>}
        </div>
      </div>
      <div>{children}</div>
    </section>
  )
}

function Stat({
  label,
  value,
  valueRaw,
  unit,
  emphasize,
}: {
  label: string
  value?: number
  valueRaw?: string
  unit?: string
  emphasize?: boolean
}) {
  const display = valueRaw ?? (value ?? 0).toLocaleString('ja-JP')
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-mono">
        {label}
      </dt>
      <dd
        className={`mt-2 font-display font-semibold tracking-tight editorial-num ${
          emphasize ? 'text-3xl md:text-4xl' : 'text-2xl md:text-3xl'
        }`}
      >
        {display}
        {unit && (
          <span className="ml-1.5 text-xs text-muted-foreground font-sans font-normal">{unit}</span>
        )}
      </dd>
    </div>
  )
}

type DistributionItem = { label: string; value: number; color: string }

function DistributionRow({
  label,
  items,
}: {
  label: string
  items: DistributionItem[]
}) {
  const total = items.reduce((sum, it) => sum + it.value, 0)

  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-mono">
          {label}
        </span>
        <span className="text-xs text-muted-foreground font-mono editorial-num">
          計 {total.toLocaleString('ja-JP')}
        </span>
      </div>

      {/* バー本体: 角を立てて編集デザイン感を出す */}
      <div className="flex h-2 overflow-hidden bg-muted">
        {total === 0 ? (
          <div className="w-full bg-muted" />
        ) : (
          items.map((it) => {
            const pct = (it.value / total) * 100
            if (pct === 0) return null
            return (
              <div
                key={it.label}
                className={`${it.color} transition-all`}
                style={{ width: `${pct}%` }}
                title={`${it.label}: ${it.value}`}
              />
            )
          })
        )}
      </div>

      {/* 凡例 */}
      <ul className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
        {items.map((it) => {
          const pct = total === 0 ? 0 : Math.round((it.value / total) * 100)
          return (
            <li key={it.label} className="flex items-baseline gap-2 min-w-0">
              <span className={`inline-block w-2 h-2 ${it.color} flex-shrink-0`} aria-hidden />
              <span className="text-muted-foreground truncate">{it.label}</span>
              <span className="font-mono editorial-num font-medium ml-auto">
                {it.value.toLocaleString('ja-JP')}
              </span>
              <span className="text-[10px] text-muted-foreground/70 font-mono editorial-num w-8 text-right">
                {pct}%
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function formatBytes(bytes: bigint): string {
  const num = Number(bytes)
  if (num < 1024) return `${num} B`
  if (num < 1024 ** 2) return `${(num / 1024).toFixed(1)} KB`
  if (num < 1024 ** 3) return `${(num / 1024 ** 2).toFixed(1)} MB`
  if (num < 1024 ** 4) return `${(num / 1024 ** 3).toFixed(2)} GB`
  return `${(num / 1024 ** 4).toFixed(2)} TB`
}
